"use client";

import React from "react";
import { Button } from "@/components/ui/button";
import { RefreshCw, FileText } from "lucide-react";
import { cn } from "@/lib/utils";
import { SummaryActions } from "@/components/ai/summary-actions";

export interface TaskAISummaryHeaderProps {
  isLoading: boolean;
  isStreaming: boolean;
  showRefreshButton: boolean;
  showClearButton: boolean;
  hasSummary: boolean;
  onRefresh: () => void;
  onGenerate: () => void;
  onClear: () => void;
  /**
   * The summary markdown. When present (and generation has finished), the shared
   * SummaryActions bar renders — copy, download (.md/.txt/PDF/audio), Slack
   * canvas, share sheet. Replaces the Slack-canvas-only button this header used
   * to carry, so a task summary offers the same actions as every other surface.
   */
  summaryMarkdown?: string | null;
  taskId?: string;
  canExport?: boolean;
  compact?: boolean;
  className?: string;
}

/**
 * Header component for task AI summary
 * Displays title, loading state, and action buttons
 */
export function TaskAISummaryHeader({
  isLoading,
  isStreaming,
  showRefreshButton,
  showClearButton,
  hasSummary,
  onRefresh,
  onGenerate,
  onClear,
  summaryMarkdown,
  taskId,
  canExport = false,
  compact = false,
  className,
}: TaskAISummaryHeaderProps) {
  const isGenerating = isLoading || isStreaming;

  if (compact) {
    return (
      <div className={cn("flex items-center justify-between", className)}>
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">Task AI Summary</span>
          {isGenerating && <RefreshCw className="h-3 w-3 animate-spin text-muted-foreground" />}
        </div>
        {showRefreshButton && (
          <Button
            variant="ghost"
            size="sm"
            onClick={hasSummary ? onRefresh : onGenerate}
            disabled={isGenerating}
            title="Refresh"
            className="h-11 w-11 sm:h-8 sm:w-8"
          >
            <RefreshCw className="h-3 w-3" />
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className={cn("flex items-center justify-between", className)}>
      <div className="flex items-center gap-2 min-w-0">
        <FileText className="h-5 w-5 shrink-0 text-blue-500" />
        <div className="min-w-0">
          <h3 className="text-base md:text-lg font-semibold">Task AI Summary</h3>
          <p className="text-xs md:text-sm text-muted-foreground">
            AI analysis of task details and notes for insights and recommendations
          </p>
        </div>
        {isGenerating && (
          <RefreshCw className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
        )}
      </div>
      <div className="flex items-center gap-1 md:gap-2 shrink-0">
        {canExport && summaryMarkdown && (
          <SummaryActions
            title="Task AI Summary"
            markdown={summaryMarkdown}
            entityType="tasks"
            entityId={taskId}
          />
        )}
        {showRefreshButton && (
          <Button
            variant="ghost"
            size="sm"
            onClick={hasSummary ? onRefresh : onGenerate}
            disabled={isGenerating}
            title="Refresh"
            className="h-11 w-11 sm:h-8 sm:w-8"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        )}
        {showClearButton && hasSummary && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClear}
            disabled={isGenerating}
            className="h-11 sm:h-8"
          >
            Clear
          </Button>
        )}
      </div>
    </div>
  );
}
